import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {HttpResponse, HttpErrorResponse} from '@angular/common/http';
import {EditableUserProfile, UserUpdatePayload} from 'app/models/user-profile-snapshot.model';
import {hasUserRevision, readProfileUser, userProfileErrorKey, USER_PROFILE_ERROR} from 'app/services/user-profile-snapshot';
import {Component, OnInit, OnDestroy} from '@angular/core';
import {UserService} from '../../../services/user.service';
import {ActivatedRoute} from '@angular/router';
import {User} from '../../../models/user.model';
import {ConfirmationPopupComponent} from '../view-group/popups/confirmation-popup/confirmation-popup.component';
import {NgbDateParserFormatter, NgbDateStruct, NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {AdminService} from '../../../services/admin.service';
import {ApiService} from '../../../services/api.service';
import {TimeFormatterService} from '../../../services/time-formatter.service';
import {LanguageSwitcher} from '../../../header/language-switcher/language-switcher.model';
import {AddDepartments} from '../../../modals/addDepartments/add-departments.component';

const PROFILE_EMPTY_TEXT = '';
const PROFILE_MISSING_ROLE = -1;
const PROFILE_ROLE_PREFIX = 'ROLE_';
const PROFILE_ROLE_FLAGS = {ADMIN: 'isAdmin', HEAD: 'isHead', PARTNER: 'isPartner',
  USER: 'isUser', ADMIN_SPECIALIZATION: 'isSpec', STOREKEEPER: 'isStorekeeper'};
const PROFILE_ACTIVE_STATUS = 'ACTIVE';
const PROFILE_INACTIVE_STATUS = 'INACTIVE';
const PROFILE_FLAGS = {testingOff: 'DISABLE_TESTING', testingOn: 'ENABLE_TESTING',
  accessOn: 'ALLOW_REMOTE_ACCESS', accessOff: 'DISABLE_REMOTE_ACCESS',
  clothingOff: 'DISABLE_CLOTHING_ISSUE', clothingOn: 'ALLOW_CLOTHING_ISSUE'};
const PROFILE_ROUTE_ID = 'id';
const PROFILE_MONTH_OFFSET = 1;
const PROFILE_CONFIRMATION = 'DO_YOU_WANT_TO_EDIT_THIS_USER';
const PROFILE_CONFIRM_ACCEPTED = 'ok';
const PROFILE_NAME_SEPARATOR = ' ';

@Component({
  selector: 'app-edit-user-profile',
  templateUrl: './edit-user-profile.component.html',
  styleUrls: ['./edit-user-profile.component.css']
})
export class EditUserProfileComponent implements OnInit, OnDestroy {
  profileLoading = false;
  profileRequiresReload = false;
  profileErrorKey = PROFILE_EMPTY_TEXT;
  private profileDestroyed = false;
  private confirmation: NgbModalRef;
  private profileDestroyed$ = new Subject<void>();
  isSubmited = true;
  isLoaded = false;
  disableRegBtn = false;
  public user: any;
  public wrapperUser: any;

  public editSelf = false;

  public selectedLanguage: string;
  public activeStatus = true;
  public inactiveStatus = false;
  public successInfo = false;
  public errorInfo = false;

  public dateOfEmployment: NgbDateStruct;
  public dateOfDismissal: NgbDateStruct;

  public selectedStatus: string;
  public selectedTesting = 'DISABLE_TESTING';
  public yesTestingStatus = false;
  public noTestingStatus = true;
  public yesTesting;
  public selectedAccess = 'DISABLE_REMOTE_ACCESS';
  public yesAccessStatus = false;
  public noAccessStatus = true;
  public selectedClothingIssue = 'ALLOW_CLOTHING_ISSUE';
  public clothingIssueDisabledStatus = false;
  public clothingIssueAllowedStatus = true;

  public isAdmin = false;
  public isHead = false;
  public isPartner = false;
  public isUser = false;
  public isSpec = false;
  public isStorekeeper = false;

  public userLocal: User;
  public isLocalAdmin = false;

  selectedGroup = 'Select';
  private groupIDForm = null;

  errorGetDataListInfo = false;
  private scheduleForm = null;
  private positionForm = null;
  groupList;

  selectedSchedule = 'Select';
  schedulesList: any[] = [];
  schedulesListNoFormat: any;

  showDepartments = false;
  noValidation = false;
  noValidationText = 'OOMFAFII';
  addedGroups;

  mainGroup = '';

  isCheckTimetable;

  isCheckTimetablePF;

  isOutsourcing;

  constructor(public userService: UserService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private adminService: AdminService,
    public parserFormatter: NgbDateParserFormatter,
    public apiService: ApiService,
    private formatService: TimeFormatterService,
    public languageSwitcher: LanguageSwitcher) {
  }

  ngOnInit(): void {
    this.loadProfileSchedules();
    this.reloadUserProfile();
  }

  get profileEditingDisabled(): boolean {
    return this.disableRegBtn || this.profileLoading || this.profileRequiresReload;
  }

  ngOnDestroy(): void {
    this.profileDestroyed = true;
    if (this.confirmation) { this.confirmation.dismiss(); }
    this.profileDestroyed$.next();
    this.profileDestroyed$.complete();
  }

  private loadProfileSchedules(): void {
    this.adminService.getSchedules().takeUntil(this.profileDestroyed$).subscribe(
      response => this.schedulesListNoFormat = response.body,
      () => { this.errorGetDataListInfo = true; });
  }

  reloadUserProfile(): void {
    if (this.disableRegBtn || this.profileLoading || this.profileDestroyed) { return; }
    const userId = this.route.snapshot.params[PROFILE_ROUTE_ID];
    this.profileLoading = true;
    this.adminService.getUserProfileSnapshot(userId).takeUntil(this.profileDestroyed$).subscribe(snapshot => {
      this.profileLoading = false;
      if (this.route.snapshot.params[PROFILE_ROUTE_ID] !== userId) { return; }
      this.groupList = snapshot.groupsWithPositions;
      this.showDepartments = !!this.groupList.length;
      this.acceptProfileUser(snapshot.user);
      this.successInfo = false;
    }, () => {
      this.profileLoading = false;
      this.isLoaded = this.profileRequiresReload = true;
      this.profileErrorKey = USER_PROFILE_ERROR.reload;
    });
  }

  private acceptProfileUser(user: EditableUserProfile): void {
    this.user = user;
    this.userLocal = this.userService.getLocal();
    this.editSelf = user.id === this.userLocal.id;
    this.isLocalAdmin = this.isAdminUser(this.userLocal);
    this.mainGroup = user.mainGroup || PROFILE_EMPTY_TEXT;
    this.selectedLanguage = user.language;
    this.isOutsourcing = user.isOutsourcing;
    this.isCheckTimetablePF = user.payrollMethod;
    this.isCheckTimetable = user.checkTimetable;
    this.applyProfileRoles(user.roles || []);
    this.applyProfileFlags(user);
    this.applyProfileDatesAndSchedule(user);
    this.profileRequiresReload = this.errorInfo = this.noValidation = false;
    this.profileErrorKey = PROFILE_EMPTY_TEXT;
    this.isLoaded = true;
  }

  private applyProfileRoles(roles: string[]): void {
    Object.keys(PROFILE_ROLE_FLAGS).forEach(role => {
      this[PROFILE_ROLE_FLAGS[role]] = roles.indexOf(role) !== PROFILE_MISSING_ROLE ||
        roles.indexOf(PROFILE_ROLE_PREFIX + role) !== PROFILE_MISSING_ROLE;
    });
  }

  private applyProfileFlags(user: EditableUserProfile): void {
    this.selectedStatus = user.status;
    this.activeStatus = user.status === PROFILE_ACTIVE_STATUS;
    this.inactiveStatus = user.status === PROFILE_INACTIVE_STATUS;
    this.changeTesting(user.isTestingDisabled ? PROFILE_FLAGS.testingOff : PROFILE_FLAGS.testingOn);
    this.changeRemove(user.isRemoteAccessAllowed ? PROFILE_FLAGS.accessOn : PROFILE_FLAGS.accessOff);
    this.changeClothingIssue(user.isClothingIssueDisabled ? PROFILE_FLAGS.clothingOff : PROFILE_FLAGS.clothingOn);
  }

  private applyProfileDatesAndSchedule(user: EditableUserProfile): void {
    this.dateOfEmployment = this.profileDate(user.dateOfEmployment);
    this.dateOfDismissal = this.profileDate(user.dateOfDismissal);
    if (user.schedule) { this.selectedSched(user.schedule.id, user.schedule.workStart, user.schedule.workEnd); }
    else { this.selectNullSchedule(); }
  }

  private profileDate(value: string): NgbDateStruct {
    if (!value) { return null; }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null :
      {year: date.getFullYear(), month: date.getMonth() + PROFILE_MONTH_OFFSET, day: date.getDate()};
  }

  postiruem(tumbler) {
       this.adminService.postiruem(this.user.id, tumbler).subscribe(
        (response) => {
          this.ngOnInit();
        },
      );
  }

  outsourcing(tumbler) {
    this.adminService.outsorce(this.user.id, tumbler).subscribe(
     (response) => {
       this.ngOnInit();
     },
   );
}

  selectedLang(selectedLanguage: string) {
    this.selectedLanguage = selectedLanguage;
    this.user.language = this.selectedLanguage;
  }

  addDepartments() {
    const modalRef = this.modalService.open(AddDepartments);
    modalRef.result.then((result) => {
      if (result) {
        const pushingDepartment = result.department;
        pushingDepartment['positions'] = result.positions;
        for (let i = 0; i < this.groupList.length; i++) {
          if (this.groupList[i].name === pushingDepartment.name) {
            this.groupList.splice(i, 1);
            break;
          }
        }
        this.groupList.push(pushingDepartment);
        if (!this.isEmptyObject(this.groupList) && this.mainGroup === '') {
          this.mainGroup = this.groupList[0]['id'];
        }
        if (this.isEmptyObject(this.groupList)) {
          this.showDepartments = false;
        } else {
          this.showDepartments = true;
        }
      }
    });
  }

  deleteItem(id) {
    for (let i = 0; i < this.groupList.length; i++) {
      if (this.groupList[i].id === id) {
        this.groupList.splice(i, 1);
        if (this.mainGroup === id) {
          this.mainGroup = '';
        }
      }
    }
    if (this.mainGroup === '' && !this.isEmptyObject(this.groupList)) {
      this.mainGroup = this.groupList[0].id;
    }
    if (this.isEmptyObject(this.groupList)) {
      this.showDepartments = false;
    } else {
      this.showDepartments = true;
    }
  }

  changeIsAdmin() {
    if (this.isAdmin === true) {
      this.isAdmin = false;
      if (this.isHead === false) {
        if (this.isPartner === true) {
          this.noAccessStatus = false;
          this.yesAccessStatus = true;
          this.selectedAccess = 'ALLOW_REMOTE_ACCESS';
        } else {
          this.noAccessStatus = true;
          this.yesAccessStatus = false;
          this.selectedAccess = 'DISABLE_REMOTE_ACCESS';
        }
      }
    } else {
      this.isAdmin = true;
    }
  }

  changeIsHead() {
    if (this.isHead === true) {
      this.isHead = false;
      if (this.isAdmin === false) {
        if (this.isPartner === true) {
          this.noAccessStatus = false;
          this.yesAccessStatus = true;
          this.selectedAccess = 'ALLOW_REMOTE_ACCESS';
        } else {
          this.noAccessStatus = true;
          this.yesAccessStatus = false;
          this.selectedAccess = 'DISABLE_REMOTE_ACCESS';
        }
      }
    } else {
      this.isHead = true;
    }
  }

  changeIsPartner() {
    if (this.isPartner === true) {
      this.isPartner = false;
    } else if (this.isPartner === false) {
      if (this.isUser === true) {
        this.isUser = false;
      }
      this.isPartner = true;
      this.noAccessStatus = false;
      this.yesAccessStatus = true;
      this.selectedAccess = 'ALLOW_REMOTE_ACCESS';
    }
  }

  changeIsUser() {
    if (this.isUser === true) {
      this.isUser = false;
    } else {
      if (this.isPartner === true) {
        this.isPartner = false;
      }
      this.isUser = true;
      this.noAccessStatus = true;
      this.yesAccessStatus = false;
      this.selectedAccess = 'DISABLE_REMOTE_ACCESS';
    }
  }

  changeIsSpec() {
    if (this.isSpec === true) {
      this.isSpec = false;
    } else {
      this.isSpec = true;
    }
  }

  changeIsStorekeeper() {
    if (this.isStorekeeper === true) {
      this.isStorekeeper = false;
    } else {
      this.isStorekeeper = true;
    }
  }

  changeTime(status) {
    if(status === 'ON') {
      this.isCheckTimetable = true;
    }
    if(status === 'OFF') {
      this.isCheckTimetable = false;
    }
  }

  changeTesting(selectedTesting: string) {
    if (selectedTesting === 'ENABLE_TESTING') {
      this.selectedTesting = selectedTesting;
      this.noTestingStatus = true;
      this.yesTestingStatus = false;
      this.yesTesting = true;
    }
    if (selectedTesting === 'DISABLE_TESTING') {
      this.selectedTesting = selectedTesting;
      this.noTestingStatus = false;
      this.yesTestingStatus = true;
      this.yesTesting = false;
    }
  }

  changeRemove(selectedAccess: string) {
    this.selectedAccess = selectedAccess;
    if (selectedAccess === 'DISABLE_REMOTE_ACCESS') {
      this.user.isRemoteAccessAllowed = false;
      this.noAccessStatus = true;
      this.yesAccessStatus = false;
    }
    if (selectedAccess === 'ALLOW_REMOTE_ACCESS') {
      this.noAccessStatus = false;
      this.yesAccessStatus = true;
      this.user.isRemoteAccessAllowed = true;
    }
  }

  changeClothingIssue(selectedClothingIssue: string) {
    this.selectedClothingIssue = selectedClothingIssue;
    if (selectedClothingIssue === 'DISABLE_CLOTHING_ISSUE') {
      this.clothingIssueDisabledStatus = true;
      this.clothingIssueAllowedStatus = false;
      this.user.isClothingIssueDisabled = true;
    }
    if (selectedClothingIssue === 'ALLOW_CLOTHING_ISSUE') {
      this.clothingIssueDisabledStatus = false;
      this.clothingIssueAllowedStatus = true;
      this.user.isClothingIssueDisabled = false;
    }
  }

  selectedStat(selectedStat: string) {
    this.selectedStatus = selectedStat;
    this.user.status = selectedStat;
    if (this.selectedStatus === 'ACTIVE') {
      this.activeStatus = true;
      this.inactiveStatus = false;
    }
    if (this.selectedStatus === 'INACTIVE') {
      this.activeStatus = false;
      this.inactiveStatus = true;
    }
  }



  selectedSched(id, start, end) {
    this.selectedSchedule = start + ' - ' + end;
    this.scheduleForm = id;
  }

  selectNullSchedule() {
    this.selectedSchedule = 'Not assigned';
    this.scheduleForm = null;
  }


  validateFields() {
    return true;
  }

  validateEmailAndPhone() {
    // Email;
    if (this.user.email) {
      if (this.validateEmail(this.user.email)) {
        return this.validatePhone(this.user.phone); // phone;
      } else {
        this.noValidationText = 'THE_EMAIL_FORMAT_IS_NOT_VALID';
        return false;
      }
    } else {
      return this.validatePhone(this.user.phone); // phone;
    }
  }

  // Not an appropriate name for this feature :/
  hasAnyCharacters(s) {
    if (/^[a-zA-Z]+$/.test(s)) {
      return true;
    }
  }

  // Email;
  validateEmail(email) {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }

  // Phone;
  validatePhone(phone) {
    if (this.user.phone) {
      const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
      if (re.test(phone)) {
        return re.test(phone);
      } else {
        this.noValidationText = 'THE_PHONE_FORMAT_IS_NOT_VALID';
        return re.test(phone);
      }
    } else {
      return true;
    }
  }

  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;

  }

  isEmptyObject(obj) {
    return (obj && (Object.keys(obj).length === 0));
  }



  submit(): void {
    if (this.disableRegBtn || this.profileLoading || this.profileRequiresReload || this.profileDestroyed) { return; }
    if (!hasUserRevision(this.user) || !Array.isArray(this.groupList)) { this.rejectProfileSave(); return; }
    this.successInfo = this.errorInfo = false;
    this.noValidation = !this.validateFields();
    if (this.noValidation) { return; }
    const payload = this.captureProfilePayload();
    this.disableRegBtn = true;
    this.confirmation = this.modalService.open(ConfirmationPopupComponent);
    this.confirmation.componentInstance.massage = PROFILE_CONFIRMATION;
    this.confirmation.result.then(result => {
      this.confirmation = null;
      if (this.profileDestroyed) { return; }
      if (result === PROFILE_CONFIRM_ACCEPTED) { this.sendProfile(payload); }
      else { this.disableRegBtn = false; }
    }, () => { this.confirmation = null; this.disableRegBtn = false; });
  }

  private captureProfilePayload(): UserUpdatePayload {
    const user: EditableUserProfile = Object.assign({}, this.user, {
      roles: Object.keys(PROFILE_ROLE_FLAGS).filter(role => this[PROFILE_ROLE_FLAGS[role]]),
      dateOfEmployment: this.parserFormatter.format(this.dateOfEmployment),
      dateOfDismissal: this.dateOfDismissal ? this.parserFormatter.format(this.dateOfDismissal) : PROFILE_EMPTY_TEXT,
      schedule: this.scheduleForm != null ? {id: this.scheduleForm} : null,
      mainGroup: this.mainGroup, isTestingDisabled: this.yesTestingStatus,
      isRemoteAccessAllowed: this.yesAccessStatus, isClothingIssueDisabled: this.clothingIssueDisabledStatus,
      checkTimetable: this.isCheckTimetable
    });
    return JSON.parse(JSON.stringify({user: user, groupsWithPositions: this.groupList}));
  }

  private sendProfile(payload: UserUpdatePayload): void {
    this.isSubmited = false;
    this.wrapperUser = payload;
    this.adminService.updateUser(payload).takeUntil(this.profileDestroyed$).subscribe(
      response => this.acceptProfileSave(response, payload), error => this.rejectProfileSave(error));
  }

  private acceptProfileSave(response: HttpResponse<EditableUserProfile>, payload: UserUpdatePayload): void {
    if (this.user.id !== payload.user.id || this.user.userRevision !== payload.user.userRevision) {
      this.rejectProfileSave(); return;
    }
    let user: EditableUserProfile;
    try { user = readProfileUser(response, payload.user.id); } catch (error) { this.rejectProfileSave(); return; }
    this.acceptProfileUser(user);
    this.groupList = payload.groupsWithPositions;
    this.disableRegBtn = false;
    this.isSubmited = this.successInfo = true;
    this.refreshLocalProfile(user);
  }

  private refreshLocalProfile(user: EditableUserProfile): void {
    if (user.id !== this.userService.getLocal().id) { return; }
    const localUser = this.userService.getLocal();
    localUser.language = user.language;
    localUser.fullName = user.firstName + PROFILE_NAME_SEPARATOR + user.lastName;
    this.userService.setLocal(localUser);
    this.languageSwitcher.setLanguage(user.language);
  }

  private rejectProfileSave(error?: HttpErrorResponse): void {
    this.disableRegBtn = this.successInfo = this.noValidation = false;
    this.isSubmited = this.errorInfo = true;
    this.profileErrorKey = userProfileErrorKey(error);
    this.profileRequiresReload = this.profileErrorKey !== USER_PROFILE_ERROR.membership &&
      this.profileErrorKey !== USER_PROFILE_ERROR.payroll;
  }

  changeMain(id) {
    this.mainGroup = id;
  }

  private isAdminUser(user: User): boolean {
    if (!user) {
      return false;
    }
    if (user.role === 'ROLE_ADMIN' || user.role === 'ADMIN') {
      return true;
    }
    if (!user.roles || !user.roles.length) {
      return false;
    }
    return user.roles.includes('ROLE_ADMIN') || user.roles.includes('ADMIN');
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/edit-user-profile/edit-user-profile.component.ts